import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getMapSettings, getColourSettings, getUnitsSettings, getAirportSettings, saveMapSettings } from '../utils/mapStyles';
import { getColumnConfig, applyPreset, updateTableConfig, getTableConfig } from '../utils/columnConfig';
import { getThemeSettings, saveThemeSettings, applyTheme, getResolvedTheme, saveCustomThemeVariables } from '../utils/themeConfig';

// Context for sharing settings across components without individual polling
const SettingsContext = createContext(null);

// Single polling interval for settings changes (instead of per-component)
const SETTINGS_POLL_INTERVAL = 500;

// Mono's greyscale map colours are part of the theme itself now
// (THEME_MAP_APPEARANCE.mono in utils/mapStyles.js). The old approach —
// hardcoding overrides here, stashing the previous colours under
// 'originalColoursBeforeMono' and writing them back on the way out — wrote one
// theme's colours over the next theme's, which is what made switching themes
// lose the right colours. Appearance is resolved per-theme on read instead, so
// there is nothing to save, override or restore.

export function SettingsProvider({ children }) {
  const [mapSettings, setMapSettings] = useState(() => getMapSettings());
  const [colourSettings, setColourSettings] = useState(() => getColourSettings());
  const [unitsSettings, setUnitsSettings] = useState(() => getUnitsSettings());
  const [columnConfig, setColumnConfig] = useState(() => getColumnConfig());
  const [airportSettings, setAirportSettings] = useState(() => getAirportSettings());
  const [themeSettings, setThemeSettings] = useState(() => getThemeSettings());

  // Apply theme on mount and when it changes
  useEffect(() => {
    applyTheme(themeSettings.themeId);
  }, [themeSettings.themeId]);

  // Single interval to poll for settings changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      // Map appearance is stored per theme under 'mapAppearance:<themeId>';
      // behaviour toggles share the global 'mapPrefs' key.
      if (e.key === 'mapPrefs' || (e.key && e.key.startsWith('mapAppearance:'))) {
        setMapSettings(getMapSettings());
        setColourSettings(getColourSettings());
      }
      if (e.key === 'displayUnits' || e.key === 'rangeRingsCount' || e.key === 'rangeRingsInterval') {
        setUnitsSettings(getUnitsSettings());
      }
      if (e.key === 'columnConfig') {
        setColumnConfig(getColumnConfig());
      }
      if (e.key === 'airportSettings') {
        setAirportSettings(getAirportSettings());
      }
      if (e.key === 'appTheme' || e.key === 'customThemeVariables') {
        const newThemeSettings = getThemeSettings();
        setThemeSettings(newThemeSettings);
        applyTheme(newThemeSettings.themeId);
        // The theme owns the map's appearance, so re-resolve both against it.
        setMapSettings(getMapSettings());
        setColourSettings(getColourSettings());
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Single interval to check for same-tab changes
    const interval = setInterval(() => {
      // Check map settings
      const currentMapSettings = getMapSettings();
      setMapSettings(prev => {
        if (prev.style !== currentMapSettings.style || prev.brightness !== currentMapSettings.brightness) {
          return currentMapSettings;
        }
        return prev;
      });

      // Check colour settings
      const currentColourSettings = getColourSettings();
      setColourSettings(prev => {
        // Quick comparison - check a few key properties
        if (prev.maxDistanceOutline !== currentColourSettings.maxDistanceOutline ||
            prev.showRangeRings !== currentColourSettings.showRangeRings ||
            prev.zoomOnAircraftSelect !== currentColourSettings.zoomOnAircraftSelect ||
            prev.showRangeOutline !== currentColourSettings.showRangeOutline ||
            prev.showAllTimeRangeOutline !== currentColourSettings.showAllTimeRangeOutline ||
            prev.showNotams !== currentColourSettings.showNotams ||
            prev.showGroundVehicles !== currentColourSettings.showGroundVehicles ||
            prev.aircraftNormal !== currentColourSettings.aircraftNormal) {
          return currentColourSettings;
        }
        return prev;
      });

      // Check units settings
      const currentUnitsSettings = getUnitsSettings();
      setUnitsSettings(prev => {
        if (prev.units !== currentUnitsSettings.units ||
            prev.rangeRingsCount !== currentUnitsSettings.rangeRingsCount ||
            prev.rangeRingsInterval !== currentUnitsSettings.rangeRingsInterval) {
          return currentUnitsSettings;
        }
        return prev;
      });

      // Check column config
      const currentColumnConfig = getColumnConfig();
      setColumnConfig(prev => {
        if (prev.activePreset !== currentColumnConfig.activePreset ||
            JSON.stringify(prev.tables) !== JSON.stringify(currentColumnConfig.tables)) {
          return currentColumnConfig;
        }
        return prev;
      });

      // Check airport settings
      const currentAirportSettings = getAirportSettings();
      setAirportSettings(prev => {
        if (prev.display !== currentAirportSettings.display ||
            prev.colour !== currentAirportSettings.colour) {
          return currentAirportSettings;
        }
        return prev;
      });

      // Check theme settings
      const currentThemeSettings = getThemeSettings();
      setThemeSettings(prev => {
        if (prev.themeId !== currentThemeSettings.themeId) {
          applyTheme(currentThemeSettings.themeId);
          return currentThemeSettings;
        }
        return prev;
      });
    }, SETTINGS_POLL_INTERVAL);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  // Provide a way for Settings page to notify of changes immediately
  const refreshSettings = useCallback(() => {
    setMapSettings(getMapSettings());
    setColourSettings(getColourSettings());
    setUnitsSettings(getUnitsSettings());
    setColumnConfig(getColumnConfig());
    setAirportSettings(getAirportSettings());
    const currentThemeSettings = getThemeSettings();
    setThemeSettings(currentThemeSettings);
    applyTheme(currentThemeSettings.themeId);
  }, []);

  // Theme update callback
  const updateTheme = useCallback((themeId, mapStyleOverride = null) => {
    const newSettings = { themeId, mapStyleOverride };
    saveThemeSettings(newSettings);
    setThemeSettings(newSettings);
    applyTheme(themeId);

    // Only an explicit style choice is persisted, and it is stored against the
    // theme being switched TO. Writing the theme's own defaults here would pin
    // them as user overrides, so the theme could never change its own look
    // again — re-resolving is enough.
    if (mapStyleOverride) {
      saveMapSettings({ style: mapStyleOverride });
    }

    setMapSettings(getMapSettings());
    setColourSettings(getColourSettings());
  }, []);

  // Custom theme update callback
  const updateCustomTheme = useCallback((variables) => {
    saveCustomThemeVariables(variables);
    if (themeSettings.themeId === 'custom') {
      applyTheme('custom');
    }
  }, [themeSettings.themeId]);

  // Get current resolved theme
  const getCurrentTheme = useCallback(() => {
    return getResolvedTheme(themeSettings.themeId);
  }, [themeSettings.themeId]);

  // Column config update callbacks
  const setPreset = useCallback((presetName) => {
    const newConfig = applyPreset(presetName);
    if (newConfig) {
      setColumnConfig(newConfig);
    }
    return newConfig;
  }, []);

  const updateTableColumns = useCallback((tableName, visibleColumns, columnOrder) => {
    const newConfig = updateTableConfig(tableName, visibleColumns, columnOrder);
    setColumnConfig(newConfig);
    return newConfig;
  }, []);

  const getTableColumnConfig = useCallback((tableName) => {
    return getTableConfig(tableName);
  }, []);

  const value = {
    mapSettings,
    colourSettings,
    unitsSettings,
    columnConfig,
    airportSettings,
    themeSettings,
    refreshSettings,
    setPreset,
    updateTableColumns,
    getTableColumnConfig,
    updateTheme,
    updateCustomTheme,
    getCurrentTheme
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

// Hook to use settings
export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}

// Hook for just units (most common use case)
export function useUnitsSettings() {
  const { unitsSettings } = useSettings();
  return unitsSettings;
}

// Hook for just colours
export function useColourSettings() {
  const { colourSettings } = useSettings();
  return colourSettings;
}

// Hook for just map settings
export function useMapSettings() {
  const { mapSettings } = useSettings();
  return mapSettings;
}

// Hook for column configuration
export function useColumnConfig() {
  const { columnConfig, setPreset, updateTableColumns, getTableColumnConfig } = useSettings();
  return { columnConfig, setPreset, updateTableColumns, getTableColumnConfig };
}

// Hook for airport settings
export function useAirportSettings() {
  const { airportSettings } = useSettings();
  return airportSettings;
}

// Hook for theme settings
export function useThemeSettings() {
  const { themeSettings, updateTheme, updateCustomTheme, getCurrentTheme } = useSettings();
  return { themeSettings, updateTheme, updateCustomTheme, getCurrentTheme };
}
